/**
 * Design System - Brand Configuration
 * 
 * Centralized brand identity, colors, assets, and guidelines.
 * This is the single source of truth for all brand-related constants.
 * 
 * Usage:
 *   import { brand } from '../design-system/brand/brandConfig';
 *   <div style={{ color: brand.colors.primary }} />
 */

import { colors } from '../tokens/colors';
import { APP_DOMAIN, APP_URL, APP_URL_WWW } from '../../core/utils/config';

/**
 * Brand Identity
 * 
 * Core brand information and metadata.
 */
export const brandIdentity = {
  /**
   * Brand Name
   */
  name: 'MyVitae',
  
  /**
   * Domain Information
   */
  domain: APP_DOMAIN,           // "myvitae.ai"
  url: APP_URL,                 // "https://myvitae.ai"
  urlWww: APP_URL_WWW,          // "https://www.myvitae.ai"
  
  /**
   * Tagline / Mission
   */
  tagline: 'Create Your Professional Portfolio',
  mission: 'Empowering professionals to showcase their achievements and build their personal brand.',
  
  /**
   * Brand Values
   */
  values: [
    'Professional Excellence',
    'User-Centric Design',
    'Innovation & Technology',
    'Accessibility for All',
  ],
};

/**
 * Brand Colors
 * 
 * Primary and accent colors that define the brand's visual identity.
 * References the color tokens from tokens/colors.js
 */
export const brandColors = {
  /**
   * Primary Brand Color (Blue)
   * 
   * Represents: Trust, professionalism, technology, reliability
   * Usage: Primary buttons, CTAs, headers, navigation, links
   * 
   * Key shades:
   * - 400 (#60a5fa): Icons, secondary buttons
   * - 600 (#2563eb): Primary buttons, main brand color
   * - 800 (#1e40af): Gradient ends, dark themes
   */
  primary: colors.primary,
  
  /**
   * Accent Brand Color (Green)
   * 
   * Represents: Success, growth, positive actions, vitality
   * Usage: Success messages, "Create" buttons, positive metrics, status indicators
   * 
   * Key shades:
   * - 400 (#4ade80): Success buttons, positive CTAs
   * - 500 (#22c55e): Default success state
   * - 600 (#16a34a): Hover states
   */
  accent: colors.accent,
  
  /**
   * Neutral Colors (Grays)
   * 
   * Usage: Text, borders, backgrounds, UI elements
   */
  neutral: colors.neutral,
  
  /**
   * Semantic Colors
   * 
   * Usage: Status messages, alerts, contextual feedback
   */
  semantic: colors.semantic,
  
  /**
   * Brand Gradients
   * 
   * Signature gradients used in hero sections, navbars, and key brand moments.
   */
  gradients: colors.gradients,
};

/**
 * Brand Assets
 * 
 * Logo files, icons, and other visual assets.
 * All paths are relative to the public folder.
 */
export const brandAssets = {
  /**
   * Logo Variants
   * 
   * Different logo formats for various contexts and backgrounds.
   */
  logos: {
    /**
     * No Background - Primary logo with transparent background
     * Use for: Light backgrounds, general use
     */
    noBackground: '/myvitae_no_background.png',
    
    /**
     * White Background - Logo with white background
     * Use for: Formal documents, print materials
     */
    whiteBackground: '/myvitae_white_background.png',
    
    /**
     * Dark Circle - Logo in dark circular badge
     * Use for: Dark backgrounds, app icons
     */
    darkCircle: '/myvitae_dark_circle.png',
    
    /**
     * Light Circle - Logo in light circular badge
     * Use for: Light backgrounds, social media profiles
     */
    lightCircle: '/myvitae_light_circle.png',
    
    /**
     * Just Text - Text-only logo
     * Use for: Horizontal space constraints, simple branding
     */
    justText: '/myvitae_just_text.png',
    
    /**
     * Symbol SVG - Icon/symbol only (vector)
     * Use for: Favicons, small icons, scalable contexts
     */
    symbol: '/myvitae_symbol.svg',
  },
  
  /**
   * Favicon
   * 
   * Browser tab icon
   */
  favicon: '/favicon.ico',
  
  /**
   * App Icons
   * 
   * For PWA and mobile app installations
   */
  appIcons: {
    icon192: '/logo192.png',
    icon512: '/logo512.png',
  },
};

/**
 * Logo Usage Guidelines
 * 
 * Rules and best practices for logo usage.
 */
export const logoGuidelines = {
  /**
   * Minimum Sizes
   * Logos should never be smaller than these dimensions
   */
  minSizes: {
    horizontal: {
      width: '120px',
      height: '32px',
    },
    icon: {
      width: '32px',
      height: '32px',
    },
  },
  
  /**
   * Clear Space
   * Minimum clear space around the logo (in terms of logo height)
   */
  clearSpace: '0.5x',  // Half the logo height on all sides
  
  /**
   * Preferred Colors
   * Recommended color combinations for the logo
   */
  preferredColors: {
    primary: {
      icon: brandColors.primary[400],      // #60a5fa
      text: brandColors.primary[600],      // #2563eb
    },
    onDark: {
      icon: brandColors.primary[400],      // #60a5fa
      text: '#ffffff',                     // White text on dark backgrounds
    },
    onLight: {
      icon: brandColors.primary[600],      // #2563eb
      text: brandColors.primary[600],      // #2563eb
    },
  },
  
  /**
   * What NOT to do
   * Common logo usage mistakes to avoid
   */
  avoid: [
    'Do not stretch or distort the logo',
    'Do not rotate the logo',
    'Do not use colors outside the brand palette',
    'Do not place on busy or low-contrast backgrounds',
    'Do not add effects like drop shadows or outlines',
    'Do not separate the icon from the text unless using the icon variant',
  ],
};

/**
 * Color Usage Guidelines
 * 
 * When and how to use brand colors effectively.
 */
export const colorGuidelines = {
  /**
   * Primary Blue Usage
   */
  primary: {
    use: [
      'Primary call-to-action buttons',
      'Navigation bars and headers',
      'Links and interactive elements',
      'Brand moments and key features',
      'Loading states and progress indicators',
    ],
    avoid: [
      'Large background areas (use sparingly)',
      'Body text (too vibrant for reading)',
      'Error or warning states',
    ],
  },
  
  /**
   * Accent Green Usage
   */
  accent: {
    use: [
      'Success messages and confirmations',
      'Positive actions (Create, Publish, Save)',
      'Growth metrics and positive trends',
      'Active/online status indicators',
      'Completion states',
    ],
    avoid: [
      'Primary navigation (reserve for blue)',
      'Neutral informational messages',
      'Destructive actions',
    ],
  },
  
  /**
   * Neutral Gray Usage
   */
  neutral: {
    use: [
      'Body text and headings',
      'Borders and dividers',
      'Background shades',
      'Disabled states',
      'Secondary UI elements',
    ],
    avoid: [
      'Large solid blocks (can feel heavy)',
      'Interactive CTAs (use brand colors)',
    ],
  },
  
  /**
   * Gradient Usage
   */
  gradients: {
    use: [
      'Hero sections and landing pages',
      'Navigation bars (primary gradient)',
      'Large header areas',
      'Authentication pages',
      'Special promotional sections',
    ],
    avoid: [
      'Small UI components',
      'Text (readability issues)',
      'Overuse throughout the app',
      'Mixing with other gradients',
    ],
  },
};

/**
 * Accessibility Guidelines
 * 
 * Ensuring brand colors meet accessibility standards.
 */
export const accessibilityGuidelines = {
  /**
   * Contrast Ratios
   * WCAG 2.1 Level AA requires:
   * - Normal text: 4.5:1
   * - Large text (18px+): 3:1
   */
  contrastRequirements: {
    normalText: '4.5:1',
    largeText: '3:1',
  },
  
  /**
   * Safe Text Combinations
   * Pre-verified color combinations that meet accessibility standards
   */
  safeTextCombinations: [
    { text: brandColors.neutral[900], background: brandColors.neutral[50] },   // Dark text on light
    { text: brandColors.neutral[50], background: brandColors.primary[600] },   // Light text on primary
    { text: brandColors.neutral[50], background: brandColors.primary[800] },   // Light text on dark primary
    { text: brandColors.neutral[900], background: brandColors.accent[100] },   // Dark text on light accent
    { text: brandColors.neutral[50], background: brandColors.accent[600] },    // Light text on dark accent
  ],
  
  /**
   * Focus Indicators
   * Always provide visible focus states for keyboard navigation
   */
  focusIndicators: {
    required: true,
    minContrast: '3:1',
    recommendedStyle: '3px solid ring with brand color',
  },
};

/**
 * Consolidated Brand Export
 * 
 * Main export containing all brand configuration.
 */
export const brand = {
  identity: brandIdentity,
  colors: brandColors,
  assets: brandAssets,
  guidelines: {
    logo: logoGuidelines,
    colors: colorGuidelines,
    accessibility: accessibilityGuidelines,
  },
};

/**
 * Default export
 */
export default brand;
